import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminLayout, useAdminApi } from '@/components/AdminLayout';

const PLANS = ['free', 'starter', 'growth', 'scale'];
const PAGE_SIZE = 25;

export default function AdminUsersPage() {
  const api = useAdminApi();
  const [role, setRole] = useState('all');
  const [data, setData] = useState({ users: [], total: 0 });
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    if (!api) return;
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (role !== 'all') params.role = role;
    if (query) params.search = query;
    api.get('/users', { params }).then((r) => setData(r.data)).catch(() => toast.error('Failed to load users'));
  }, [api, role, query, page]);

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  useEffect(() => { load(); }, [load]);

  const update = async (id, payload, msg) => {
    try {
      await api.put(`/users/${id}`, payload);
      toast.success(msg);
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between" data-testid="admin-users-page">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Users</h1>
          <p className="text-gray-400" data-testid="admin-users-total">{data.total} account{data.total === 1 ? '' : 's'}</p>
        </div>
        <div className="flex gap-3">
        <Input placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-64 bg-[#0a1a12] border-white/20 text-white" data-testid="admin-users-search" />
        <Select value={role} onValueChange={(v) => { setRole(v); setPage(0); }}>
          <SelectTrigger className="w-40 bg-[#0a1a12] border-white/20 text-white" data-testid="admin-users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="user">Business users</SelectItem>
            <SelectItem value="ca">CA partners</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>
      <div className="rounded-lg border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Business / Plan</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className="border-t border-white/5" data-testid={`admin-user-row-${u.id}`}>
                <td className="p-3 text-white">{u.name}</td>
                <td className="p-3 text-gray-300">{u.email}</td>
                <td className="p-3"><Badge variant="outline" className="text-gray-300 border-white/20">{u.role}</Badge></td>
                <td className="p-3">
                  {u.business ? (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-300 truncate max-w-[140px]">{u.business.name}</span>
                      <Select value={u.business.plan || 'free'} onValueChange={(p) => update(u.id, { plan: p }, 'Plan updated')}>
                        <SelectTrigger className="h-8 w-28 bg-transparent border-white/20 text-white" data-testid={`admin-user-plan-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                      {u.business.is_trial && <Badge className="bg-[#EF9F27]/20 text-[#EF9F27] border-0" data-testid={`admin-user-trial-${u.id}`}>trial</Badge>}
                    </div>
                  ) : <span className="text-gray-600">-</span>}
                </td>
                <td className="p-3">
                  <span className={u.is_active === false ? 'text-red-400' : 'text-[#1D9E75]'}>{u.is_active === false ? 'Disabled' : 'Active'}</span>
                </td>
                <td className="p-3 text-right">
                  {u.role !== 'admin' && (
                    <Button size="sm" variant="ghost" data-testid={`admin-user-toggle-${u.id}`}
                      className="text-gray-300 hover:text-white hover:bg-white/10"
                      onClick={() => update(u.id, { is_active: u.is_active === false }, u.is_active === false ? 'User enabled' : 'User disabled')}>
                      {u.is_active === false ? 'Enable' : 'Disable'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {data.users.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-500">No users found</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-3 text-sm text-gray-400">
        <span data-testid="admin-users-page-info">Page {page + 1} of {pages}</span>
        <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)}
          className="text-gray-300 hover:text-white hover:bg-white/10" data-testid="admin-users-prev">Previous</Button>
        <Button size="sm" variant="ghost" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}
          className="text-gray-300 hover:text-white hover:bg-white/10" data-testid="admin-users-next">Next</Button>
      </div>
    </AdminLayout>
  );
}
